import React, { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import { meetingService, Meeting, TitrationSchedule, TitrationWeek } from '../../services/meetingService';
import { MedicationType } from '../../services/servicesService';
import { meetingTextService } from '../../services/meetingTextService';
import { MeetingType } from '../../services/meetingTypeService';
import { patientService, Patient, PatientSummaryItem, TitrationMeasurement } from '../../services/patientService';
import { medicationService, Medication } from '../../services/medicationService';
import { meetingMedicationService } from '../../services/meetingMedicationService';
import { signatureRxService } from '../../services/signatureRxService';
import { diagnosesService, Diagnosis } from '../../services/diagnosesService';
import { MeetingActionBar } from '../ui/MeetingActionBar';
import { MedicationSection, MedicationSectionHandle } from '../medications/MedicationSection';
import {
  TitrationTreatmentTab,
  TreatmentDecisionConfirmDialog,
  TitrationTreatmentMedication,
  EMPTY_TREATMENT_MEDICATION,
  toTreatmentMedication,
  treatmentValidationError,
  TREATMENT_DECISIONS_WITH_REASONS,
  TREATMENT_DECISIONS_NEEDING_CONFIRMATION,
} from './TitrationTreatmentTab';
import { Select, MenuItem, FormControl } from '@mui/material';
import { questionnaireService } from '../../services/questionnaireService';
import { QuestionnaireType, PatientQuestionnaire } from '../../types/questionnaire.types';
import { GenericQuestionnaireWizard } from '../questionnaires/GenericQuestionnaireWizard';
import TruncatedSelect from '../common/TruncatedSelect';
import { ViewQuestionnaireModal } from '../modals/ViewQuestionnaireModal';

const CM_INTAKE_AGENDA: { title: string; subsections: { name: string; questions: string[] }[] }[] = [
  {
    title: '1. Introduction & Purpose of Assessment',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'What made you seek an ADHD assessment now?',
          'What difficulties are you hoping to understand or improve?',
          'Have you previously been assessed or treated for mental health or ADHD?',
        ],
      },
    ],
  },
  {
    title: '2. Current ADHD-Related Difficulties',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'What are the main difficulties you experience day-to-day?',
          'Do you struggle with attention, organisation, restlessness, impulsivity, or emotional regulation?',
          'Which symptoms affect you the most currently?',
          'When do you notice these difficulties most?',
        ],
      },
    ],
  },
  {
    title: '3. Functional Impairment',
    subsections: [
      {
        name: 'Work / Studies',
        questions: [
          'Do these difficulties affect work or studies?',
          'Do you struggle with deadlines, organisation, or completing tasks?',
          'Have symptoms affected performance or attendance?',
        ],
      },
      {
        name: 'Relationships',
        questions: [
          'Do symptoms affect relationships or communication?',
          'Do others describe you as forgetful, distracted, or impulsive?',
        ],
      },
      {
        name: 'Daily Functioning',
        questions: [
          'Do you struggle with routines, appointments, finances, or household tasks?',
          'Do tasks feel overwhelming or difficult to start?',
        ],
      },
    ],
  },
  {
    title: '4. Childhood History',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'What were you like at school?',
          'Did teachers or parents raise concerns about attention, behaviour, or organisation?',
          'Were you forgetful, restless, impulsive, or easily distracted as a child?',
          'Did you struggle academically or socially?',
        ],
      },
    ],
  },
  {
    title: '5. Mental Health Review',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'Have you experienced anxiety, depression, or burnout?',
          'Any history of trauma, panic attacks, or emotional instability?',
          'Any previous psychiatric diagnoses?',
          'How is your sleep generally?',
        ],
      },
    ],
  },
  {
    title: '6. Risk & Substance Use Review',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'Any history of self-harm or suicidal thoughts?',
          'Any history of psychosis, mania, or severe mood episodes?',
          'Do you currently use alcohol or recreational drugs?',
          'Have substances caused difficulties in functioning or mental health?',
        ],
      },
    ],
  },
  {
    title: '7. Medical & Medication History',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'Any medical conditions or neurological issues?',
          'Are you currently taking medications?',
          'Any cardiac history or seizures?',
          'Any family history of ADHD or mental health conditions?',
        ],
      },
    ],
  },
  {
    title: '8. DIVA-5 Structured Clinical Review',
    subsections: [
      {
        name: 'During This Section',
        questions: [
          'Review Inattention criteria',
          'Review Hyperactivity / Impulsivity criteria',
          'Review childhood and adult examples',
          'Assess functional impairment for each criterion',
        ],
      },
      {
        name: 'Clinician Actions',
        questions: [
          'Mark: Criteria Met',
          'Mark: Criteria Not Met',
          'Mark: Insufficient Evidence',
          'Add supporting examples and notes',
        ],
      },
    ],
  },
  {
    title: '9. Summary & Next Steps',
    subsections: [
      {
        name: 'Questions',
        questions: [
          'Is there anything important we have not discussed?',
          'Do you have questions about the next steps?',
        ],
      },
      {
        name: 'Clinician Explains',
        questions: [
          'Psychiatrist will review the assessment',
          'Additional information may be required',
          'This is not the final diagnosis',
          'Treatment suitability is assessed separately',
        ],
      },
    ],
  },
];

const ADHD_DECISION_OPTIONS = [
  { value: 'ADHD_NOT_DIAGNOSED', label: 'ADHD Not Diagnosed' },
  { value: 'NO_ADHD_MEDICATION_RECOMMENDED', label: 'No ADHD medication recommended' },
  { value: 'ADHD_MEDICATION_RECOMMENDED', label: 'ADHD medication recommended' },
  { value: 'ADHD_MEDICATION_RECOMMENDED_DEFERRED', label: 'ADHD medication recommended — deferred' },
];

const TITRATION_OPTIONS = [
  { value: 'STANDARD_TITRATION', label: 'Standard titration' },
  { value: 'SLOW_TITRATION', label: 'Slow titration' },
];

const MONITORING_OPTIONS = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'TWICE_WEEKLY', label: 'Twice weekly' },
];

const DEFERRAL_REASON_OPTIONS = [
  'ECG required',
  'Blood tests required',
  'GP medical info required',
  'Psychiatric history clarification required',
  'Other specialist opinion',
  'Other',
];

const SIDE_EFFECT_OPTIONS = [
  { value: 'reduced_appetite', label: 'Reduced appetite' },
  { value: 'difficulty_sleeping', label: 'Difficulty sleeping' },
  { value: 'increased_heart_rate', label: 'Increased heart rate' },
  { value: 'dry_mouth', label: 'Dry mouth' },
  { value: 'headache', label: 'Headache' },
  { value: 'other', label: 'Other' },
];

const DEFAULT_TITRATION_WEEKS = 4;

// Build/resize the weekly schedule rows, preserving existing entries.
/**
 * The titration meeting a follow-up carries on from: the patient's latest done
 * titration intake or follow-up scheduled no later than this one. The backend
 * copies its Titration tab into a new follow-up; the panel uses the same rule to
 * prefill follow-ups created before that, and to seed the Treatment tab.
 */
const findPreviousTitrationMeeting = (current: Meeting, meetings: Meeting[]): Meeting | undefined =>
  meetings
    .filter((m) =>
      m.id !== current.id &&
      (m.meeting_type?.type === 'titration_intake' || m.meeting_type?.type === 'titration_followup') &&
      (m.scheduled_at || '') <= (current.scheduled_at || '\uffff')
    )
    .sort((a, b) => (b.scheduled_at || '').localeCompare(a.scheduled_at || ''))[0];

const buildWeeks = (duration: number, existing: TitrationWeek[] = []): TitrationWeek[] => {
  const n = Math.max(0, Math.min(52, Math.floor(duration) || 0));
  return Array.from({ length: n }, (_, i) => existing[i] || { week: i + 1, dose: '', instructions: '' });
};

interface MeetingDetailPanelProps {
  meeting: Meeting;
  meetingTypes: MeetingType[];
  patient: Patient;
  onClose: () => void;
  onMeetingUpdated: () => void;
  onNoShow: (meeting: Meeting) => void;
  onFinishTreatment: (meeting: Meeting) => void;
  onSendEmail: (meeting: Meeting) => void;
  onViewSummary?: (meeting: Meeting) => void;
  onPsySummaryReady?: (meeting: Meeting) => void;
  onCmSummaryReady?: (meeting: Meeting) => void;
  patientSummaries?: PatientSummaryItem[];
  medicationType?: MedicationType | null;
  clinicLanguage?: string;
  /** Bumped by the parent after a summary is approved, to re-read the meeting. */
  refreshKey?: number;
}

export const MeetingDetailPanel: React.FC<MeetingDetailPanelProps> = ({
  meeting,
  meetingTypes,
  patient,
  onClose,
  onMeetingUpdated,
  onNoShow,
  onFinishTreatment,
  onSendEmail,
  onViewSummary,
  onPsySummaryReady,
  onCmSummaryReady,
  patientSummaries = [],
  refreshKey = 0,
  medicationType,
  clinicLanguage,
}) => {
  const isHebrew = clinicLanguage === 'he';
  const [meetingDetailsTab, setMeetingDetailsTab] = useState<'summary' | 'agenda' | 'questionnaire' | 'treatment' | 'titration'>('summary');
  // Measurement row -> the submission it came from, opened from the row label.
  const [viewedMeasurementQuestionnaire, setViewedMeasurementQuestionnaire] =
    useState<{ id: number; title: string } | null>(null);
  const [agendaObject, setAgendaObject] = useState<any>(null);
  const [isAgendaLoading, setIsAgendaLoading] = useState(false);
  const [questionnaireTypesForMeeting, setQuestionnaireTypesForMeeting] = useState<QuestionnaireType[]>([]);
  const [patientQuestionnairesForMeeting, setPatientQuestionnairesForMeeting] = useState<PatientQuestionnaire[]>([]);
  const [isQuestionnairesLoading, setIsQuestionnairesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [activeQuestionnaireId, setActiveQuestionnaireId] = useState<number | null>(null);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [selectedSubsectionIndex, setSelectedSubsectionIndex] = useState(0);
  const [editedComments, setEditedComments] = useState('');
  const [editedTitrationComments, setEditedTitrationComments] = useState('');
  const [editedSummaryAndDiscussion, setEditedSummaryAndDiscussion] = useState('');
  const [editedRecommendations, setEditedRecommendations] = useState('');
  const [summaryHeight, setSummaryHeight] = useState(80);
  const [psySummaryHeight, setPsySummaryHeight] = useState(65);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [isPsyDraggingDivider, setIsPsyDraggingDivider] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [selectedSummaryKey, setSelectedSummaryKey] = useState<string>('');
  const [fullMeeting, setFullMeeting] = useState<Meeting>(meeting);

  // Treatment state
  const [primaryDiagnostic, setPrimaryDiagnostic] = useState('');
  const [primaryDiagSearch, setPrimaryDiagSearch] = useState('');
  const [primaryDiagResults, setPrimaryDiagResults] = useState<Diagnosis[]>([]);
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false);
  const [secondaryDiagnostics, setSecondaryDiagnostics] = useState<{ code: string; label: string }[]>([]);
  const [secondaryDiagSearch, setSecondaryDiagSearch] = useState('');
  const [secondaryDiagResults, setSecondaryDiagResults] = useState<Diagnosis[]>([]);
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);
  const [diagnosticNotes, setDiagnosticNotes] = useState('');
  const [adhdMedicationDecision, setAdhdMedicationDecision] = useState('');
  const [adhdNotDiagnosedReason, setAdhdNotDiagnosedReason] = useState('');
  const [noAdhdMedicationReasonAndNotes, setNoAdhdMedicationReasonAndNotes] = useState('');
  const [adhdMedicationDeferredReasonAndNotes, setAdhdMedicationDeferredReasonAndNotes] = useState('');
  const [adhdMedicationDeferredReasons, setAdhdMedicationDeferredReasons] = useState<string[]>([]);
  const [medicationId, setMedicationId] = useState<number | null>(null);
  const [titrationPlan, setTitrationPlan] = useState('');
  const [initialPlanNotes, setInitialPlanNotes] = useState('');
  const [monitoringPlan, setMonitoringPlan] = useState('');
  const [additionalMonitoringNotes, setAdditionalMonitoringNotes] = useState('');
  const [needFollowUp, setNeedFollowUp] = useState<boolean | null>(null);
  const [followUpWeeks, setFollowUpWeeks] = useState<number | null>(null);
  const [medications, setMedications] = useState<Medication[]>([]);

  // Titration tab state
  const [titMedicationId, setTitMedicationId] = useState<number | null>(null);
  const [titDosageInstruction, setTitDosageInstruction] = useState('');
  const [titQuantity, setTitQuantity] = useState<number | null>(null);
  const [titDurationWeeks, setTitDurationWeeks] = useState<number>(DEFAULT_TITRATION_WEEKS);
  const [titSameDose, setTitSameDose] = useState(false);
  const [titWeeks, setTitWeeks] = useState<TitrationWeek[]>([]);
  const [titHowToTake, setTitHowToTake] = useState('');
  const [titSideEffects, setTitSideEffects] = useState<string[]>([]);
  const [titSideEffectsOther, setTitSideEffectsOther] = useState('');
  const [titMeasurements, setTitMeasurements] = useState<TitrationMeasurement[]>([]);
  const [creatingPrescription, setCreatingPrescription] = useState(false);

  // Treatment tab state for a titration follow-up (the PSY body above has its own)
  const [treatmentDecision, setTreatmentDecision] = useState('');
  const [treatmentDecisionReasons, setTreatmentDecisionReasons] = useState<string[]>([]);
  const [treatmentDecisionNotes, setTreatmentDecisionNotes] = useState('');
  const [showTreatmentValidationError, setShowTreatmentValidationError] = useState(false);
  const [treatmentMedication, setTreatmentMedication] =
    useState<TitrationTreatmentMedication>(EMPTY_TREATMENT_MEDICATION);
  // The meeting_medications row backing the Treatment tab, null until it is saved once.
  const [treatmentMedicationRowId, setTreatmentMedicationRowId] = useState<number | null>(null);
  // Which decision is waiting on the confirmation dialog, and what it will do next.
  const [pendingTreatmentAction, setPendingTreatmentAction] = useState<'save' | 'finish' | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const psyContainerRef = useRef<HTMLDivElement>(null);
  const primaryDiagTouchedRef = useRef(false);
  const secondaryDiagTouchedRef = useRef(false);
  const medicationSectionRef = useRef<MedicationSectionHandle>(null);
  const summaryPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const meetingTypeCategory = meeting.meeting_type?.type || null;
  const isCMMeeting =
    meetingTypeCategory === 'case_manager_intake' ||
    meetingTypeCategory === 'case_manager_followup';
  const isPSYMeeting =
    meetingTypeCategory === 'psy_intake' ||
    meetingTypeCategory === 'psy_followup';
  const isTitrationMeeting =
    meetingTypeCategory === 'titration_intake' ||
    meetingTypeCategory === 'titration_followup';
  // The Treatment tab is the follow-up's decision on the titration — the intake
  // has nothing to decide yet, so it keeps the Titration tab only.
  const isTitrationFollowUpMeeting = meetingTypeCategory === 'titration_followup';
  const meetingTypeName = meeting.meeting_type?.name || 'Meeting';

  // Fetch full meeting data on mount or when meeting.id changes
  useEffect(() => {
    meetingService.getMeeting(meeting.id).then(async (data) => {
      primaryDiagTouchedRef.current = false;
      secondaryDiagTouchedRef.current = false;
      setFullMeeting(data);
      setEditedComments(data.cm_comments || '');
      setEditedTitrationComments(data.titration_comments || '');
      setEditedSummaryAndDiscussion(data.psy_summary_and_discussion || '');
      setEditedRecommendations(data.psy_recommendations || '');
      // Treatment fields
      setPrimaryDiagnostic(data.primary_diagnostic || '');
      if (data.primary_diagnostic) {
        diagnosesService.searchDiagnoses(data.primary_diagnostic).then((results) => {
          const match = results.find((d) => d.code === data.primary_diagnostic);
          setPrimaryDiagSearch(match ? `${data.primary_diagnostic} — ${match.Diagnosis}` : data.primary_diagnostic!);
        }).catch(() => setPrimaryDiagSearch(data.primary_diagnostic!));
      } else {
        setPrimaryDiagSearch('');
      }
      setSecondaryDiagSearch('');
      setDiagnosticNotes(data.diagnostic_notes || '');
      setAdhdMedicationDecision(data.adhd_medication_decision || '');
      setAdhdNotDiagnosedReason(data.adhd_not_diagnosed_reason || '');
      setNoAdhdMedicationReasonAndNotes(data.no_adhd_medication_reason_and_notes || '');
      setAdhdMedicationDeferredReasonAndNotes(data.adhd_medication_deferred_reason_and_notes || '');
      setAdhdMedicationDeferredReasons(data.adhd_medication_deferred_reasons || []);
      setMedicationId(data.medication_id);
      setTitrationPlan(data.titration_plan || '');
      setInitialPlanNotes(data.initial_plan_notes || '');
      setMonitoringPlan(data.monitoring_plan || '');
      setAdditionalMonitoringNotes(data.additional_monitoring_notes || '');
      setNeedFollowUp(data.need_follow_up ?? null);
      setFollowUpWeeks(data.follow_up_weeks ?? null);
      // Treatment tab of a titration follow-up
      setTreatmentDecision(data.treatment_decision || '');
      setTreatmentDecisionReasons(data.treatment_decision_reasons || []);
      setTreatmentDecisionNotes(data.treatment_decision_notes || '');
      setShowTreatmentValidationError(false);
      // Resolve full labels for saved secondary diagnostics
      if (data.secondary_diagnostic) {
        const codes = data.secondary_diagnostic.split(',').map((c) => c.trim()).filter(Boolean);
        const items = await Promise.all(
          codes.map(async (code) => {
            try {
              const results = await diagnosesService.searchDiagnoses(code);
              const match = results.find((d) => d.code === code);
              return { code, label: match ? `${code} — ${match.Diagnosis}` : code };
            } catch {
              return { code, label: code };
            }
          })
        );
        setSecondaryDiagnostics(items);
      } else {
        setSecondaryDiagnostics([]);
      }

      // Titration schedule
      const applyTitrationSchedule = (ts: TitrationSchedule) => {
        const dur = ts.durationWeeks || DEFAULT_TITRATION_WEEKS;
        setTitMedicationId(ts.medication?.medication_id ?? null);
        setTitDosageInstruction(ts.medication?.dosageInstruction || '');
        setTitQuantity(ts.medication?.quantity ?? null);
        setTitDurationWeeks(dur);
        setTitSameDose(!!ts.sameDoseAllWeeks);
        setTitWeeks(buildWeeks(dur, ts.weeks || []));
        setTitHowToTake(ts.howToTake || '');
        setTitSideEffects(ts.sideEffects || []);
        setTitSideEffectsOther(ts.sideEffectsOther || '');
      };
      // A follow-up created before the backend copied the previous plan into it
      // has none of its own — carry the previous titration meeting's plan over.
      let ts = data.titration_schedule;
      if (!ts && isTitrationFollowUpMeeting) {
        try {
          const done = await meetingService.getMeetings(patient.tenant_id, patient.id, undefined, 'done');
          ts = findPreviousTitrationMeeting(data, done.filter((m) => m.titration_schedule))
            ?.titration_schedule ?? null;
        } catch {
          ts = null;
        }
      }
      if (ts) {
        applyTitrationSchedule(ts);
      } else if (isTitrationMeeting) {
        // First time on this meeting: defaults + prefill medication from the psy intake
        setTitDurationWeeks(DEFAULT_TITRATION_WEEKS);
        setTitSameDose(false);
        setTitWeeks(buildWeeks(DEFAULT_TITRATION_WEEKS));
        setTitDosageInstruction('');
        setTitQuantity(null);
        setTitHowToTake('');
        setTitSideEffects([]);
        setTitSideEffectsOther('');
        try {
          const meetings = await meetingService.getMeetings(patient.tenant_id, patient.id, undefined, 'done');
          const psyIntake = meetings
            .filter((m) => m.meeting_type?.type === 'psy_intake' && m.medication_id != null)
            .sort((a, b) => (b.scheduled_at || '').localeCompare(a.scheduled_at || ''))[0];
          setTitMedicationId(psyIntake?.medication_id ?? null);
        } catch {
          setTitMedicationId(null);
        }
      }
    });
    if (isPSYMeeting || isTitrationMeeting) {
      medicationService.getMedications().then(setMedications).catch(() => {});
    }
    if (isTitrationMeeting) {
      patientService.getTitrationMeasurements(patient.id).then(setTitMeasurements).catch(() => {});
    }
  }, [meeting.id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Treatment tab medication: the follow-up's own row when it has one, otherwise
  // seeded from the previous titration meeting (intake or follow-up) so the
  // clinician confirms the medication rather than retyping it.
  useEffect(() => {
    if (!isTitrationFollowUpMeeting) return;
    let cancelled = false;
    (async () => {
      try {
        const own = await meetingMedicationService.getMeetingMedications(meeting.id);
        if (cancelled) return;
        if (own.length > 0) {
          setTreatmentMedicationRowId(own[0].id);
          setTreatmentMedication(toTreatmentMedication(own[0]));
          return;
        }
        setTreatmentMedicationRowId(null);
        setTreatmentMedication(EMPTY_TREATMENT_MEDICATION);
        const meetings = await meetingService.getMeetings(patient.tenant_id, patient.id, undefined, 'done');
        const previous = findPreviousTitrationMeeting(meeting, meetings);
        if (cancelled || !previous) return;
        const previousMeds = await meetingMedicationService.getMeetingMedications(previous.id);
        if (cancelled) return;
        const seeded = previousMeds.length > 0
          ? toTreatmentMedication(previousMeds[0])
          : { ...EMPTY_TREATMENT_MEDICATION };
        // The previous meeting also records the prescription in its titration
        // schedule — fall back to that for anything the medication row lacks.
        const scheduled = previous.titration_schedule?.medication;
        if (scheduled) {
          if (seeded.medication_type_id == null) seeded.medication_type_id = scheduled.medication_id;
          if (!seeded.dosage_instructions) seeded.dosage_instructions = scheduled.dosageInstruction || '';
          if (!seeded.quantity) seeded.quantity = scheduled.quantity != null ? String(scheduled.quantity) : '';
        }
        setTreatmentMedication(seeded);
      } catch {
        // The prefill is a convenience — leave the fields empty if it fails.
      }
    })();
    return () => { cancelled = true; };
  }, [meeting.id, refreshKey, isTitrationFollowUpMeeting]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the weekly schedule row count in sync with the titration duration.
  useEffect(() => {
    setTitWeeks((prev) => buildWeeks(titDurationWeeks, prev));
  }, [titDurationWeeks]);

  // Fetch agenda MeetingText when meeting changes
  useEffect(() => {
    setSelectedSectionIndex(0);
    setSelectedSubsectionIndex(0);
    const fetchAgenda = async () => {
      setIsAgendaLoading(true);
      try {
        const texts = await meetingTextService.getMeetingTexts(meeting.id, 'agenda');
        if (texts.length > 0) {
          const parsed = JSON.parse(texts[0].content);
          setAgendaObject(parsed);
        } else {
          setAgendaObject(null);
        }
      } catch {
        setAgendaObject(null);
      } finally {
        setIsAgendaLoading(false);
      }
    };
    fetchAgenda();
  }, [meeting.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch questionnaire types + patient questionnaires when questionnaire tab is opened (CM meetings only)
  useEffect(() => {
    if (meetingDetailsTab !== 'questionnaire' || !isCMMeeting) return;
    const typeIds = fullMeeting.meeting_type?.questionnaire_type_ids;
    if (!typeIds || typeIds.length === 0) return;
    setIsQuestionnairesLoading(true);
    Promise.all([
      questionnaireService.getQuestionnaireTypes(meeting.tenant_id),
      questionnaireService.getPatientQuestionnaires(meeting.tenant_id, patient.id, undefined, undefined, 0, 1000),
    ]).then(([allTypes, patientQResult]) => {
      const ordered = typeIds
        .map((id) => allTypes.find((qt) => qt.id === id))
        .filter((qt): qt is QuestionnaireType => qt !== undefined);
      setQuestionnaireTypesForMeeting(ordered);
      setPatientQuestionnairesForMeeting(patientQResult.items);
      if (ordered.length > 0) {
        setSelectedTypeId(ordered[0].id);
        loadActiveQuestionnaire(ordered[0].id, patientQResult.items);
      }
    })
      .catch(() => toast.error('Failed to load questionnaires'))
      .finally(() => setIsQuestionnairesLoading(false));
  }, [meetingDetailsTab, isCMMeeting]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadActiveQuestionnaire = useCallback(async (typeId: number, patientQs: PatientQuestionnaire[]) => {
    const existing = patientQs.find((q) => q.questionnaire_type_id === typeId);
    if (existing) {
      setActiveQuestionnaireId(existing.id);
    } else {
      try {
        const created = await questionnaireService.createPatientQuestionnaire(patient.id, typeId, meeting.tenant_id);
        setPatientQuestionnairesForMeeting((prev) => [...prev, created]);
        setActiveQuestionnaireId(created.id);
      } catch {
        toast.error('Failed to open questionnaire');
      }
    }
  }, [patient.id, meeting.tenant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first summary when patientSummaries loads
  useEffect(() => {
    if (patientSummaries.length > 0) {
      const first = patientSummaries[0];
      const firstKey = first.meeting_id != null ? `meeting-${first.meeting_id}` : 'questionnaire';
      setSelectedSummaryKey(firstKey);
    }
  }, [patientSummaries]);

  // Debounced primary diagnosis search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!primaryDiagTouchedRef.current) return;
      if (primaryDiagSearch.length >= 2) {
        try {
          const results = await diagnosesService.searchDiagnoses(primaryDiagSearch);
          setPrimaryDiagResults(results.slice(0, 20));
          setShowPrimaryDropdown(true);
        } catch {
          setPrimaryDiagResults([]);
        }
      } else {
        setPrimaryDiagResults([]);
        setShowPrimaryDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [primaryDiagSearch]);

  // Debounced secondary diagnosis search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!secondaryDiagTouchedRef.current) return;
      if (secondaryDiagSearch.length >= 2) {
        try {
          const results = await diagnosesService.searchDiagnoses(secondaryDiagSearch);
          setSecondaryDiagResults(results.slice(0, 20));
          setShowSecondaryDropdown(true);
        } catch {
          setSecondaryDiagResults([]);
        }
      } else {
        setSecondaryDiagResults([]);
        setShowSecondaryDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [secondaryDiagSearch]);

  // CM draggable divider
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingDivider || !containerRef.current) return;
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const percentage = (offsetY / rect.height) * 100;
      setSummaryHeight(Math.max(5, Math.min(95, percentage)));
    };
    const handleMouseUp = () => setIsDraggingDivider(false);

    if (isDraggingDivider) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingDivider]);

  // PSY draggable divider
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isPsyDraggingDivider || !psyContainerRef.current) return;
      const container = psyContainerRef.current;
      const rect = container.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const percentage = (offsetY / rect.height) * 100;
      setPsySummaryHeight(Math.max(5, Math.min(95, percentage)));
    };
    const handleMouseUp = () => setIsPsyDraggingDivider(false);

    if (isPsyDraggingDivider) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isPsyDraggingDivider]);

  const buildPsyUpdatePayload = useCallback(() => ({
    psy_summary_and_discussion: editedSummaryAndDiscussion,
    psy_recommendations: editedRecommendations,
    primary_diagnostic: primaryDiagnostic || null,
    secondary_diagnostic: secondaryDiagnostics.length > 0 ? secondaryDiagnostics.map((d) => d.code).join(',') : null,
    diagnostic_notes: diagnosticNotes || null,
    adhd_medication_decision: adhdMedicationDecision || null,
    adhd_not_diagnosed_reason:
      adhdMedicationDecision === 'ADHD_NOT_DIAGNOSED' ? adhdNotDiagnosedReason || null : null,
    no_adhd_medication_reason_and_notes:
      adhdMedicationDecision === 'NO_ADHD_MEDICATION_RECOMMENDED'
        ? noAdhdMedicationReasonAndNotes || null
        : null,
    adhd_medication_deferred_reasons:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED'
        ? adhdMedicationDeferredReasons.length > 0 ? adhdMedicationDeferredReasons : null
        : null,
    adhd_medication_deferred_reason_and_notes:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED'
        ? adhdMedicationDeferredReasonAndNotes || null
        : null,
    medication_id:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? medicationId : null,
    titration_plan:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? titrationPlan || null : null,
    initial_plan_notes:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? initialPlanNotes || null : null,
    monitoring_plan:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' ? monitoringPlan || null : null,
    additional_monitoring_notes:
      adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED'
        ? additionalMonitoringNotes || null
        : null,
    need_follow_up: needFollowUp,
    follow_up_weeks: needFollowUp === true ? (followUpWeeks ?? null) : null,
  }), [
    editedSummaryAndDiscussion,
    editedRecommendations,
    primaryDiagnostic,
    secondaryDiagnostics,
    diagnosticNotes,
    adhdMedicationDecision,
    adhdNotDiagnosedReason,
    noAdhdMedicationReasonAndNotes,
    adhdMedicationDeferredReasonAndNotes,
    adhdMedicationDeferredReasons,
    medicationId,
    titrationPlan,
    initialPlanNotes,
    monitoringPlan,
    additionalMonitoringNotes,
    needFollowUp,
    followUpWeeks,
  ]);

  const handleCreatePrescription = async () => {
    setCreatingPrescription(true);
    try {
      const res = await signatureRxService.getPrescribeLink(patient.id);
      window.open(res.url, '_blank', 'noopener,noreferrer');
      toast.success(
        res.created ? 'Patient created in SignatureRx' : 'Opening SignatureRx'
      );
      onMeetingUpdated?.();
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const missing = detail?.missing_fields as { key: string; label: string }[] | undefined;
      if (missing && missing.length > 0) {
        // The one failure a nurse can actually fix — name the fields.
        toast.error(
          `Cannot create prescription — missing: ${missing.map((f) => f.label).join(', ')}`
        );
      } else {
        toast.error(
          (typeof detail === 'string' && detail) ||
            detail?.message ||
            'Failed to open SignatureRx'
        );
      }
    } finally {
      setCreatingPrescription(false);
    }
  };

  const buildTitrationPayload = useCallback((): TitrationSchedule => {
    // A follow-up edits its medication on the Treatment tab; keep the schedule's
    // copy in step with it, since the summary and PDF read the schedule.
    const medication = isTitrationFollowUpMeeting
      ? {
          medication_id: treatmentMedication.medication_type_id,
          dosageInstruction: treatmentMedication.dosage_instructions,
          quantity: treatmentMedication.quantity === '' ? null : Number(treatmentMedication.quantity),
        }
      : {
          medication_id: titMedicationId,
          dosageInstruction: titDosageInstruction,
          quantity: titQuantity,
        };
    const weeks = titSameDose && titWeeks.length > 0
      ? titWeeks.map((w) => ({ week: w.week, dose: titWeeks[0].dose, instructions: titWeeks[0].instructions }))
      : titWeeks;
    return {
      medication,
      durationWeeks: titDurationWeeks,
      sameDoseAllWeeks: titSameDose,
      weeks,
      howToTake: titHowToTake,
      sideEffects: titSideEffects,
      sideEffectsOther: titSideEffects.includes('other') ? titSideEffectsOther : '',
    };
  }, [
    isTitrationFollowUpMeeting,
    treatmentMedication,
    titMedicationId,
    titDosageInstruction,
    titQuantity,
    titDurationWeeks,
    titSameDose,
    titWeeks,
    titHowToTake,
    titSideEffects,
    titSideEffectsOther,
  ]);

  // Reasons and notes belong to Stop Treatment and Additional Review Required
  // only, so changing the decision drops them instead of sending stale ones.
  const handleTreatmentDecisionChange = useCallback((value: string) => {
    setTreatmentDecision(value);
    setTreatmentDecisionReasons([]);
    setTreatmentDecisionNotes('');
    setShowTreatmentValidationError(false);
  }, []);

  const buildTreatmentPayload = useCallback(() => {
    const carriesReasons = TREATMENT_DECISIONS_WITH_REASONS.includes(treatmentDecision);
    return {
      treatment_decision: treatmentDecision || null,
      treatment_decision_reasons: carriesReasons ? treatmentDecisionReasons : [],
      treatment_decision_notes: carriesReasons ? treatmentDecisionNotes : '',
    };
  }, [treatmentDecision, treatmentDecisionReasons, treatmentDecisionNotes]);

  // The Treatment tab medication is a meeting_medications row of its own — one
  // per follow-up, created the first time the meeting is saved.
  const saveTreatmentMedication = useCallback(async () => {
    if (!isTitrationFollowUpMeeting) return;
    // Dose and unit are deliberately absent: the Treatment tab no longer edits
    // them, so sending them would overwrite whatever the intake prescribed with
    // nulls. The columns stay on meeting_medications for the prescription flow.
    const payload = {
      medication_type_id: treatmentMedication.medication_type_id,
      dosage_instructions: treatmentMedication.dosage_instructions || null,
      quantity: treatmentMedication.quantity === '' ? null : Number(treatmentMedication.quantity),
    };
    if (treatmentMedicationRowId != null) {
      await meetingMedicationService.updateMeetingMedication(treatmentMedicationRowId, payload);
      return;
    }
    const isEmpty =
      payload.medication_type_id == null &&
      !payload.dosage_instructions &&
      payload.quantity == null;
    if (isEmpty) return;
    const created = await meetingMedicationService.createMeetingMedication(fullMeeting.id, {
      meeting_id: fullMeeting.id,
      ...payload,
    });
    setTreatmentMedicationRowId(created.id);
  }, [isTitrationFollowUpMeeting, treatmentMedication, treatmentMedicationRowId, fullMeeting.id]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      if (isCMMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, { cm_comments: editedComments });
      } else if (isPSYMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, buildPsyUpdatePayload());
        if (medicationType === 'IL') {
          await medicationSectionRef.current?.save();
        }
      } else if (isTitrationMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, {
          titration_schedule: buildTitrationPayload(),
          titration_comments: editedTitrationComments,
          ...(isTitrationFollowUpMeeting ? buildTreatmentPayload() : {}),
        });
        await saveTreatmentMedication();
      }
      toast.success('Saved successfully');
      onMeetingUpdated();
    } catch (err) {
      console.error('Failed to save meeting:', err);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [
    fullMeeting.id,
    isCMMeeting,
    isPSYMeeting,
    isTitrationMeeting,
    isTitrationFollowUpMeeting,
    editedComments,
    editedTitrationComments,
    buildPsyUpdatePayload,
    buildTitrationPayload,
    buildTreatmentPayload,
    saveTreatmentMedication,
    medicationType,
    onMeetingUpdated,
  ]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (summaryPollingRef.current) clearInterval(summaryPollingRef.current);
    };
  }, []);

  // Titration sends are explicit and separate from finishing: the summary has to
  // be opened and approved first, so nothing reaches the patient unreviewed.

  const handleFinish = useCallback(async () => {
    setIsFinishing(true);
    try {
      // Save first
      if (isCMMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, { cm_comments: editedComments });
      } else if (isPSYMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, buildPsyUpdatePayload());
        if (medicationType === 'IL') {
          await medicationSectionRef.current?.save();
        }
      } else if (isTitrationMeeting) {
        await meetingService.updateMeeting(fullMeeting.id, {
          titration_schedule: buildTitrationPayload(),
          titration_comments: editedTitrationComments,
          ...(isTitrationFollowUpMeeting ? buildTreatmentPayload() : {}),
        });
        await saveTreatmentMedication();
      }
      // Mark as done — the summary is produced by the backend: from an AI prompt
      // for CM/PSY, or built from the summary definition for titration.
      await meetingService.updateMeetingStatus(fullMeeting.id, 'done');
      toast.success(
        isTitrationMeeting ? 'Meeting finished' : 'Meeting finished and summarization started'
      );
      onMeetingUpdated();

      // Finishing never sends. The clinician opens the summary, approves it, and
      // sends it from the Send Summary button — so nothing reaches the patient
      // without a person having read it.
      if ((isPSYMeeting && onPsySummaryReady) || ((isCMMeeting || isTitrationMeeting) && onCmSummaryReady)) {
        // Poll every 3 s until the summary exists, then open the summary popup.
        const meetingId = fullMeeting.id;
        const meetingSnapshot = fullMeeting;
        const onReady = isPSYMeeting ? onPsySummaryReady! : onCmSummaryReady!;
        // PSY summaries are stored as 'summary_section'; CM and titration as 'summary'.
        const summaryTextType = isPSYMeeting ? 'summary_section' : 'summary';
        if (summaryPollingRef.current) clearInterval(summaryPollingRef.current);
        summaryPollingRef.current = setInterval(async () => {
          try {
            const texts = await meetingTextService.getMeetingTexts(meetingId, summaryTextType);
            if (texts && texts.length > 0) {
              clearInterval(summaryPollingRef.current!);
              summaryPollingRef.current = null;
              onReady(meetingSnapshot);
            }
          } catch {
            // ignore transient polling errors
          }
        }, 3000);
      }
    } catch (err) {
      console.error('Failed to finish meeting:', err);
      toast.error('Failed to finish meeting');
    } finally {
      setIsFinishing(false);
    }
  }, [
    fullMeeting,
    isCMMeeting,
    isPSYMeeting,
    isTitrationMeeting,
    isTitrationFollowUpMeeting,
    editedComments,
    editedTitrationComments,
    buildPsyUpdatePayload,
    buildTitrationPayload,
    buildTreatmentPayload,
    saveTreatmentMedication,
    medicationType,
    onMeetingUpdated,
    onPsySummaryReady,
    onCmSummaryReady,
  ]);

  // Save and Finish go through these for a titration follow-up: a reason the
  // decision requires blocks the write, and the two decisions that cannot be
  // undone from this tab ask first.
  const treatmentGateAllows = useCallback((action: 'save' | 'finish'): boolean => {
    if (!isTitrationFollowUpMeeting) return true;
    if (treatmentValidationError(treatmentDecision, treatmentDecisionReasons, treatmentDecisionNotes)) {
      setShowTreatmentValidationError(true);
      setMeetingDetailsTab('treatment');
      return false;
    }
    if (TREATMENT_DECISIONS_NEEDING_CONFIRMATION.includes(treatmentDecision)) {
      setPendingTreatmentAction(action);
      return false;
    }
    return true;
  }, [isTitrationFollowUpMeeting, treatmentDecision, treatmentDecisionReasons, treatmentDecisionNotes]);

  const requestSave = useCallback(() => {
    if (treatmentGateAllows('save')) handleSave();
  }, [treatmentGateAllows, handleSave]);

  const requestFinish = useCallback(() => {
    if (treatmentGateAllows('finish')) handleFinish();
  }, [treatmentGateAllows, handleFinish]);

  const confirmPendingTreatmentAction = useCallback(() => {
    const action = pendingTreatmentAction;
    setPendingTreatmentAction(null);
    if (action === 'save') handleSave();
    else if (action === 'finish') handleFinish();
  }, [pendingTreatmentAction, handleSave, handleFinish]);

  const selectedSummaryContent = (() => {
    const item = patientSummaries.find(s =>
      (s.meeting_id != null ? `meeting-${s.meeting_id}` : 'questionnaire') === selectedSummaryKey
    );
    return item?.content || '';
  })();

  return (
    <div className="flex flex-col h-full overflow-hidden min-h-0">
      {/* Header Bar */}
      <div className="px-0 py-0 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center justify-between w-full gap-2 py-1">
          {/* Tabs (left side) — wrap rather than clip once a meeting type has four */}
          <div className="flex items-center gap-0 flex-wrap">
            <button
              onClick={() => { setMeetingDetailsTab('summary'); }}
              className={`px-3 py-1 text-xs transition-colors border-b-2 ${
                meetingDetailsTab === 'summary'
                  ? 'text-blue-700 border-blue-700 font-medium'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => { setMeetingDetailsTab('agenda'); setSelectedSectionIndex(0); setSelectedSubsectionIndex(0); }}
              className={`px-3 py-1 text-xs transition-colors border-b-2 ${
                meetingDetailsTab === 'agenda'
                  ? 'text-blue-700 border-blue-700 font-medium'
                  : 'text-gray-600 border-transparent hover:text-gray-800'
              }`}
            >
              Agenda
            </button>
            {isCMMeeting && (
              <button
                onClick={() => setMeetingDetailsTab('questionnaire')}
                className={`px-3 py-1 text-xs transition-colors border-b-2 ${
                  meetingDetailsTab === 'questionnaire'
                    ? 'text-blue-700 border-blue-700 font-medium'
                    : 'text-gray-600 border-transparent hover:text-gray-800'
                }`}
              >
                Questionnaire
              </button>
            )}
            {isTitrationMeeting && (
              <button
                onClick={() => { setMeetingDetailsTab('titration'); }}
                className={`px-3 py-1 text-xs transition-colors border-b-2 ${
                  meetingDetailsTab === 'titration'
                    ? 'text-blue-700 border-blue-700 font-medium'
                    : 'text-gray-600 border-transparent hover:text-gray-800'
                }`}
              >
                Titration
              </button>
            )}
            {/* Last on purpose: the decision follows the titration data it is based on. */}
            {(isPSYMeeting || isTitrationFollowUpMeeting) && (
              <button
                onClick={() => { setMeetingDetailsTab('treatment'); }}
                className={`px-3 py-1 text-xs transition-colors border-b-2 ${
                  meetingDetailsTab === 'treatment'
                    ? 'text-blue-700 border-blue-700 font-medium'
                    : 'text-gray-600 border-transparent hover:text-gray-800'
                }`}
              >
                Treatment
              </button>
            )}
          </div>

          {/* Meeting type name (center) */}
          <h2 className="text-sm font-semibold text-gray-900 flex-shrink-0">{meetingTypeName}</h2>

          {/* Right side: dropdown + close */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {meetingDetailsTab === 'summary' && patientSummaries.length > 0 && (
              <TruncatedSelect
                widthClass="w-72"
                value={selectedSummaryKey}
                onChange={setSelectedSummaryKey}
                options={patientSummaries.map((item) => {
                  const key = item.meeting_id != null ? `meeting-${item.meeting_id}` : 'questionnaire';
                  const [year, month, day] = item.date.split('-');
                  const typePart = item.meeting_type_name
                    ? `${item.meeting_type_name}${item.meeting_index != null ? ` ${item.meeting_index}` : ''}`
                    : item.name;
                  return { value: key, label: `${day}/${month}/${year} - ${typePart}` };
                })}
              />
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-sm mr-1.5"
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {meetingDetailsTab === 'summary' && isCMMeeting && (
          <div className="flex flex-col flex-1 min-h-0" ref={containerRef}>
            {/* Read-only summary textarea */}
            <textarea
              value={selectedSummaryContent}
              readOnly
              dir={isHebrew ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 text-gray-900 bg-white resize-none overflow-y-auto"
              style={{ height: `${summaryHeight}%`, fontSize: '13px' }}
              placeholder="Summary will be available here"
            />

            {/* Draggable Divider */}
            <div
              onMouseDown={() => setIsDraggingDivider(true)}
              className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize transition-colors"
              title="Drag to resize"
            />

            {/* Editable comments textarea */}
            <textarea
              value={editedComments}
              onChange={(e) => setEditedComments(e.target.value)}
              dir={isHebrew ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
              style={{ height: `${100 - summaryHeight}%` }}
              placeholder="No comments available"
            />
          </div>
        )}

        {meetingDetailsTab === 'summary' && isPSYMeeting && (
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden" ref={psyContainerRef}>
            {/* Read-only summary textarea */}
            <textarea
              value={selectedSummaryContent}
              readOnly
              dir={isHebrew ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 text-gray-900 bg-white resize-none overflow-y-auto"
              style={{ height: `${psySummaryHeight}%`, fontSize: '13px' }}
              placeholder="Summary will be available here"
            />

            {/* Draggable Divider */}
            <div
              onMouseDown={() => setIsPsyDraggingDivider(true)}
              className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize"
            />

            {/* Two side-by-side editable textareas */}
            <div className="flex flex-1 overflow-hidden">
              <textarea
                value={editedSummaryAndDiscussion}
                onChange={(e) => setEditedSummaryAndDiscussion(e.target.value)}
                dir={isHebrew ? 'rtl' : 'ltr'}
                className="flex-1 px-3 py-2 border-r border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                placeholder="Summary and Discussion"
                style={{ margin: 0, padding: '8px' }}
              />
              <textarea
                value={editedRecommendations}
                onChange={(e) => setEditedRecommendations(e.target.value)}
                dir={isHebrew ? 'rtl' : 'ltr'}
                className="flex-1 px-3 py-2 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
                placeholder="Recommendations"
                style={{ margin: 0, padding: '8px' }}
              />
            </div>
          </div>
        )}

        {meetingDetailsTab === 'summary' && isTitrationMeeting && (
          <div className="flex flex-col flex-1 min-h-0" ref={containerRef}>
            {/* Read-only view of previous meeting summaries (selected via the dropdown) */}
            <textarea
              value={selectedSummaryContent}
              readOnly
              dir={isHebrew ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 text-gray-900 bg-white resize-none overflow-y-auto"
              style={{ height: `${summaryHeight}%`, fontSize: '13px' }}
              placeholder="Summary will be available here"
            />

            {/* Draggable Divider */}
            <div
              onMouseDown={() => setIsDraggingDivider(true)}
              className="w-full h-1 bg-transparent hover:bg-gray-300 cursor-row-resize transition-colors"
              title="Drag to resize"
            />

            {/* Editable comments textarea */}
            <textarea
              value={editedTitrationComments}
              onChange={(e) => setEditedTitrationComments(e.target.value)}
              dir={isHebrew ? 'rtl' : 'ltr'}
              className="w-full px-3 py-2 border border-gray-300 text-xs text-gray-700 bg-white resize-none overflow-y-auto"
              style={{ height: `${100 - summaryHeight}%` }}
              placeholder="No comments available"
            />
          </div>
        )}

        {meetingDetailsTab === 'summary' && !isCMMeeting && !isPSYMeeting && !isTitrationMeeting && (
          <div className="flex-1 flex items-center justify-center p-4">
            <p className="text-sm text-gray-500 italic">
              Summary view is not available for this meeting type.
            </p>
          </div>
        )}

        {meetingDetailsTab === 'agenda' && (
          <div className="flex flex-col h-full flex-1 overflow-hidden" dir={isHebrew ? 'rtl' : 'ltr'}>
            {meetingTypeCategory === 'case_manager_intake' ? (
              <div className="flex-1 overflow-y-auto p-5 space-y-6">
                {CM_INTAKE_AGENDA.map((section, sIdx) => (
                  <div key={sIdx}>
                    <p className="text-sm font-bold text-gray-900 mb-3">{section.title}</p>
                    {section.subsections.map((sub, subIdx) => (
                      <div key={subIdx} className="mb-3">
                        <p className="text-xs font-bold text-gray-800 mb-1">{sub.name}</p>
                        <ul className="list-disc pl-5 space-y-1">
                          {sub.questions.map((q, qIdx) => (
                            <li key={qIdx} className="text-xs text-gray-700 leading-snug">{q}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : isAgendaLoading ? (
              <p className="text-xs text-gray-500 italic p-4">Loading agenda...</p>
            ) : agendaObject?.sections ? (
              <div className="flex h-full gap-4 p-4">
                {/* Sections column */}
                <div className={`flex flex-col gap-2 overflow-y-auto w-56 border-r border-gray-200 ${isHebrew ? 'pl-4' : 'pr-4'}`}>
                  {agendaObject.sections.map((section: any, sectionIdx: number) => (
                    <button
                      key={sectionIdx}
                      onClick={() => { setSelectedSectionIndex(sectionIdx); setSelectedSubsectionIndex(0); }}
                      className={`w-full px-3 py-2 text-xs rounded transition-colors ${isHebrew ? 'text-right' : 'text-left'} whitespace-normal break-words ${
                        selectedSectionIndex === sectionIdx
                          ? 'bg-blue-100 text-blue-700 border border-blue-300 font-medium'
                          : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                      }`}
                    >
                      {section.sectionName}
                    </button>
                  ))}
                </div>

                {/* Subsections column */}
                <div className="flex flex-col gap-2 overflow-y-auto w-56">
                  {selectedSectionIndex < agendaObject.sections.length &&
                    agendaObject.sections[selectedSectionIndex].subsections?.map((subsection: any, subsectionIdx: number) => (
                      <button
                        key={subsectionIdx}
                        onClick={() => setSelectedSubsectionIndex(subsectionIdx)}
                        className={`w-full px-3 py-2 text-xs rounded transition-colors ${isHebrew ? 'text-right' : 'text-left'} whitespace-normal break-words ${
                          selectedSubsectionIndex === subsectionIdx
                            ? 'bg-green-100 text-green-700 border border-green-300 font-medium'
                            : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                        }`}
                      >
                        {subsection.subsectionName}
                      </button>
                    ))}
                </div>

                {/* Questions column */}
                <div className={`flex-1 flex flex-col border-l border-gray-200 ${isHebrew ? 'pr-4' : 'pl-4'} overflow-hidden`}>
                  <h3 className={`text-xs font-semibold text-gray-700 mb-2 ${isHebrew ? 'text-right' : 'text-left'}`}>Questions</h3>
                  <div className="flex-1 overflow-y-auto">
                    {selectedSectionIndex < agendaObject.sections.length &&
                    selectedSubsectionIndex < (agendaObject.sections[selectedSectionIndex].subsections?.length ?? 0) ? (
                      <ul className={`space-y-2 list-disc ${isHebrew ? 'pr-5' : 'pl-5'}`}>
                        {agendaObject.sections[selectedSectionIndex].subsections[selectedSubsectionIndex].questions?.map(
                          (question: string, idx: number) => (
                            <li key={idx} className={`text-xs text-gray-700 leading-tight ${isHebrew ? 'text-right' : 'text-left'}`}>
                              {question}
                            </li>
                          )
                        )}
                      </ul>
                    ) : (
                      <p className={`text-xs text-gray-500 italic ${isHebrew ? 'text-right' : 'text-left'}`}>No questions available</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic p-4">No agenda available</p>
            )}
          </div>
        )}

        {meetingDetailsTab === 'questionnaire' && isCMMeeting && (() => {
          const typeIds = fullMeeting.meeting_type?.questionnaire_type_ids;
          if (!typeIds || typeIds.length === 0) {
            return (
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-xs text-gray-500 italic">No questionnaires configured for this meeting type.</p>
              </div>
            );
          }
          if (isQuestionnairesLoading) {
            return (
              <div className="flex-1 overflow-y-auto p-4">
                <p className="text-xs text-gray-500 italic">Loading questionnaires...</p>
              </div>
            );
          }
          const selectedType = questionnaireTypesForMeeting.find((qt) => qt.id === selectedTypeId);
          return (
            <div className="flex flex-col flex-1 overflow-hidden">
              {questionnaireTypesForMeeting.length > 1 && (
                <div className="px-4 pt-3 pb-2 shrink-0">
                  <FormControl fullWidth size="small">
                    <Select
                      value={selectedTypeId ?? ''}
                      onChange={(e) => {
                        const newTypeId = e.target.value as number;
                        setSelectedTypeId(newTypeId);
                        setActiveQuestionnaireId(null);
                        loadActiveQuestionnaire(newTypeId, patientQuestionnairesForMeeting);
                      }}
                      sx={{ backgroundColor: '#EBF5F5', borderRadius: '8px' }}
                    >
                      {questionnaireTypesForMeeting.map((qt) => (
                        <MenuItem key={qt.id} value={qt.id}>{qt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
              {activeQuestionnaireId && (
                <div className="flex-1 overflow-hidden">
                  <GenericQuestionnaireWizard
                    open={true}
                    onClose={() => setActiveQuestionnaireId(null)}
                    questionnaireId={activeQuestionnaireId}
                    questionnaireName={selectedType?.name ?? ''}
                    tenantId={meeting.tenant_id}
                    isEmbedded={true}
                    onSave={() => {
                      questionnaireService.getPatientQuestionnaires(meeting.tenant_id, patient.id, undefined, undefined, 0, 1000)
                        .then((r) => setPatientQuestionnairesForMeeting(r.items))
                        .catch(() => {});
                    }}
                  />
                </div>
              )}
            </div>
          );
        })()}

        {meetingDetailsTab === 'treatment' && isPSYMeeting && (
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Section 1: Diagnosis */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                Diagnosis
              </h3>
              <div className="space-y-3">
                {/* Primary Diagnosis */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Diagnosis
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={primaryDiagSearch}
                      onChange={(e) => {
                        primaryDiagTouchedRef.current = true;
                        setPrimaryDiagSearch(e.target.value);
                        if (!e.target.value) setPrimaryDiagnostic('');
                      }}
                      onBlur={() => setTimeout(() => setShowPrimaryDropdown(false), 150)}
                      onFocus={() => primaryDiagResults.length > 0 && setShowPrimaryDropdown(true)}
                      placeholder="Search ICD-10 code or name..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    />
                    {showPrimaryDropdown && primaryDiagResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {primaryDiagResults.map((d) => (
                          <button
                            key={d.code}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:bg-gray-50"
                            onMouseDown={() => {
                              setPrimaryDiagnostic(d.code);
                              setPrimaryDiagSearch(`${d.code} — ${d.Diagnosis}`);
                              setShowPrimaryDropdown(false);
                            }}
                          >
                            <span className="font-mono text-xs text-teal-700">{d.code}</span>
                            {' — '}{d.Diagnosis}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Secondary Diagnosis (multi-select) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Diagnosis
                  </label>
                  {secondaryDiagnostics.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {secondaryDiagnostics.map((d) => (
                        <span
                          key={d.code}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-200 text-xs text-teal-800"
                        >
                          <span className="font-mono">{d.label}</span>
                          <button
                            type="button"
                            onClick={() =>
                              setSecondaryDiagnostics((prev) => prev.filter((x) => x.code !== d.code))
                            }
                            className="text-teal-500 hover:text-teal-700 leading-none"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <input
                      type="text"
                      value={secondaryDiagSearch}
                      onChange={(e) => { secondaryDiagTouchedRef.current = true; setSecondaryDiagSearch(e.target.value); }}
                      onBlur={() => setTimeout(() => setShowSecondaryDropdown(false), 150)}
                      onFocus={() => secondaryDiagResults.length > 0 && setShowSecondaryDropdown(true)}
                      placeholder="Search to add ICD-10 code..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    />
                    {showSecondaryDropdown && secondaryDiagResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {secondaryDiagResults
                          .filter((d) => !secondaryDiagnostics.some((x) => x.code === d.code))
                          .map((d) => (
                            <button
                              key={d.code}
                              type="button"
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:bg-gray-50"
                              onMouseDown={() => {
                                setSecondaryDiagnostics((prev) => [
                                  ...prev,
                                  { code: d.code, label: `${d.code} — ${d.Diagnosis}` },
                                ]);
                                setSecondaryDiagSearch('');
                                setShowSecondaryDropdown(false);
                              }}
                            >
                              <span className="font-mono text-xs text-teal-700">{d.code}</span>
                              {' — '}{d.Diagnosis}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Diagnostic Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diagnostic Notes
                  </label>
                  <textarea
                    value={diagnosticNotes}
                    onChange={(e) => setDiagnosticNotes(e.target.value)}
                    dir={isHebrew ? 'rtl' : 'ltr'}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                    placeholder="Additional diagnostic notes..."
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Medication Decision */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
                Medication Decision
              </h3>

              {medicationType === 'IL' ? (
                <>
                  <MedicationSection
                    ref={medicationSectionRef}
                    meetingId={meeting.id}
                    medicationTypes={medications}
                    showToggle={true}
                  />

                  {/* Follow-up */}
                  <div className="mt-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Need Follow Up
                      </label>
                      <div className="flex gap-4">
                        {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(({ value, label }) => (
                          <label key={label} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="needFollowUp"
                              checked={needFollowUp === value}
                              onChange={() => setNeedFollowUp(value)}
                              className="text-teal-600 focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {needFollowUp === true && (
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                          Doctor Follow Up meeting in
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={followUpWeeks ?? ''}
                          onChange={(e) => setFollowUpWeeks(e.target.value ? Number(e.target.value) : null)}
                          className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                        />
                        <span className="text-sm text-gray-700">Weeks</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="space-y-3">
                  {/* ADHD Medication Decision */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ADHD Medication Decision
                    </label>
                    <div className="space-y-2">
                      {ADHD_DECISION_OPTIONS.map(({ value, label }) => (
                        <label key={value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="adhdMedicationDecision"
                            value={value}
                            checked={adhdMedicationDecision === value}
                            onChange={() => setAdhdMedicationDecision(value)}
                            className="text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Conditional: ADHD Not Diagnosed */}
                  {adhdMedicationDecision === 'ADHD_NOT_DIAGNOSED' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason
                      </label>
                      <textarea
                        value={adhdNotDiagnosedReason}
                        onChange={(e) => setAdhdNotDiagnosedReason(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                        placeholder="Reason ADHD was not diagnosed..."
                      />
                    </div>
                  )}

                  {/* Conditional: No ADHD medication */}
                  {adhdMedicationDecision === 'NO_ADHD_MEDICATION_RECOMMENDED' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason & Notes
                      </label>
                      <textarea
                        value={noAdhdMedicationReasonAndNotes}
                        onChange={(e) => setNoAdhdMedicationReasonAndNotes(e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                        placeholder="Reason and notes for no ADHD medication..."
                      />
                    </div>
                  )}

                  {/* Conditional: ADHD medication recommended */}
                  {adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED' && (
                    <div className="space-y-3 pl-4 border-l-2 border-teal-200">
                      {/* Medication dropdown */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Medication
                        </label>
                        <select
                          value={medicationId ?? ''}
                          onChange={(e) =>
                            setMedicationId(e.target.value ? Number(e.target.value) : null)
                          }
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                        >
                          <option value="">Select medication...</option>
                          {medications.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Titration Plan */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Titration Plan
                        </label>
                        <div className="flex gap-4">
                          {TITRATION_OPTIONS.map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="titrationPlan"
                                value={value}
                                checked={titrationPlan === value}
                                onChange={() => setTitrationPlan(value)}
                                className="text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Initial Plan Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Initial Plan Notes
                        </label>
                        <textarea
                          value={initialPlanNotes}
                          onChange={(e) => setInitialPlanNotes(e.target.value)}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                          placeholder="Initial plan notes..."
                        />
                      </div>

                      {/* Monitoring Plan */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monitoring Plan
                        </label>
                        <div className="flex gap-4">
                          {MONITORING_OPTIONS.map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                name="monitoringPlan"
                                value={value}
                                checked={monitoringPlan === value}
                                onChange={() => setMonitoringPlan(value)}
                                className="text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Additional Monitoring Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Monitoring Notes
                        </label>
                        <textarea
                          value={additionalMonitoringNotes}
                          onChange={(e) => setAdditionalMonitoringNotes(e.target.value)}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                          placeholder="Additional monitoring notes..."
                        />
                      </div>
                    </div>
                  )}

                  {/* Conditional: ADHD medication deferred */}
                  {adhdMedicationDecision === 'ADHD_MEDICATION_RECOMMENDED_DEFERRED' && (
                    <div className="space-y-3 pl-4 border-l-2 border-teal-200">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Reason for deferral</label>
                        <div className="space-y-2">
                          {DEFERRAL_REASON_OPTIONS.map((reason) => (
                            <label key={reason} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={adhdMedicationDeferredReasons.includes(reason)}
                                onChange={(e) => {
                                  setAdhdMedicationDeferredReasons(prev =>
                                    e.target.checked ? [...prev, reason] : prev.filter(r => r !== reason)
                                  );
                                }}
                                className="rounded text-teal-600 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">{reason}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                        <textarea
                          value={adhdMedicationDeferredReasonAndNotes}
                          onChange={(e) => setAdhdMedicationDeferredReasonAndNotes(e.target.value)}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                          placeholder="Additional details..."
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {meetingDetailsTab === 'treatment' && isTitrationFollowUpMeeting && (
          <TitrationTreatmentTab
            isHebrew={isHebrew}
            medications={medications}
            decision={treatmentDecision}
            onDecisionChange={handleTreatmentDecisionChange}
            reasons={treatmentDecisionReasons}
            onReasonsChange={setTreatmentDecisionReasons}
            notes={treatmentDecisionNotes}
            onNotesChange={setTreatmentDecisionNotes}
            medication={treatmentMedication}
            onMedicationChange={(patch) => setTreatmentMedication((prev) => ({ ...prev, ...patch }))}
            showValidationError={showTreatmentValidationError}
            onCreatePrescription={handleCreatePrescription}
            creatingPrescription={creatingPrescription}
          />
        )}

        {meetingDetailsTab === 'titration' && isTitrationMeeting && (
          <div className="flex-1 overflow-y-auto p-4 space-y-6" dir={isHebrew ? 'rtl' : 'ltr'}>
            {/* 1. Baseline Measurements */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Baseline Measurements</h3>
              {titMeasurements.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No measurements submitted yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="text-gray-600">
                        <th className="border-b border-gray-200 px-2 py-1 text-left"></th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Blood pressure</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Heart rate</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Height (cm)</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Weight (kg)</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">BMI</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Submission date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => { let week = 0; return titMeasurements.map((m, idx) => {
                        // Label by what the submission is, not its position — the weekly
                        // monitoring rows reuse the same measurement fields as the baseline.
                        const label = m.is_baseline ? 'Base' : `Week ${++week}`;
                        // Same mechanism as the questionnaire view: the backend names the
                        // fields that failed validation, and we mark those by name.
                        const failed = new Set(m.out_of_range_fields || []);
                        const red = 'text-red-600 font-semibold';
                        // Monitoring red flags are not vitals, so nothing in the
                        // value cells shows them — the label carries it instead.
                        const flags = m.monitoring_flags || [];
                        const cell = (field: string) =>
                          `border-b border-gray-100 px-2 py-1${failed.has(field) ? ` ${red}` : ''}`;
                        return (
                        <tr key={idx} className="text-gray-900">
                          <td className="border-b border-gray-100 px-2 py-1 font-medium">
                            <button
                              type="button"
                              onClick={() => setViewedMeasurementQuestionnaire({
                                id: m.questionnaire_id,
                                title: m.questionnaire_title || label,
                              })}
                              className={
                                flags.length
                                  ? 'text-red-600 font-semibold underline hover:text-red-800'
                                  : 'text-teal-700 underline hover:text-teal-900'
                              }
                              title={
                                flags.length
                                  ? `${flags.length} red flag${flags.length > 1 ? 's' : ''}: `
                                    + flags.map((f) => f.label).join('; ')
                                  : 'View the submitted questionnaire'
                              }
                            >
                              {label}
                              {flags.length > 0 && <span aria-hidden="true"> ⚠</span>}
                              {flags.length > 0 && (
                                <span className="sr-only">
                                  {` — ${flags.length} red flag${flags.length > 1 ? 's' : ''}`}
                                </span>
                              )}
                            </button>
                          </td>
                          <td className="border-b border-gray-100 px-2 py-1">
                            {m.systolic != null && m.diastolic != null ? (
                              <>
                                <span className={failed.has('blood_pressure_systolic') ? red : undefined}>{m.systolic}</span>
                                /
                                <span className={failed.has('blood_pressure_diastolic') ? red : undefined}>{m.diastolic}</span>
                              </>
                            ) : '—'}
                          </td>
                          <td className={cell('heart_rate')}>{m.heart_rate ?? '—'}</td>
                          <td className="border-b border-gray-100 px-2 py-1">{m.height ?? '—'}</td>
                          <td className={cell('weight')}>{m.weight ?? '—'}</td>
                          <td className={cell('bmi')}>{m.bmi ?? '—'}</td>
                          <td className="border-b border-gray-100 px-2 py-1">{m.date ? m.date.split('-').reverse().join('/') : '—'}</td>
                        </tr>
                        );
                      }); })()}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* 2. Medication — a follow-up records it on the Treatment tab instead */}
            {!isTitrationFollowUpMeeting && (
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-bold text-gray-900">Medication</h3>
                <button
                  type="button"
                  onClick={handleCreatePrescription}
                  disabled={creatingPrescription}
                  title="Create the patient in SignatureRx if needed and open SignatureRx to write the prescription"
                  className="px-4 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingPrescription ? 'Opening…' : 'Create Prescription'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medication name</label>
                  <select
                    value={titMedicationId ?? ''}
                    onChange={(e) => setTitMedicationId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Select medication…</option>
                    {medications.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dosage instruction</label>
                  <input
                    type="text"
                    value={titDosageInstruction}
                    onChange={(e) => setTitDosageInstruction(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="e.g., 1 tablet in the morning"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    min={0}
                    value={titQuantity ?? ''}
                    onChange={(e) => setTitQuantity(e.target.value === '' ? null : Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="e.g., 30"
                  />
                </div>
              </div>
            </section>
            )}

            {/* 3. Titration Plan */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Titration Plan</h3>
              <label className="block text-sm font-medium text-gray-700 mb-1">Number of weeks until the next follow-up appointment</label>
              <input
                type="number"
                min={1}
                max={52}
                value={titDurationWeeks}
                onChange={(e) => setTitDurationWeeks(Math.max(0, Number(e.target.value) || 0))}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
              />
            </section>

            {/* 4. Weekly Medication Schedule */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 mb-2">Weekly Medication Schedule</h3>
              <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={titSameDose}
                  onChange={(e) => setTitSameDose(e.target.checked)}
                />
                Apply the same dose and instructions for all weeks
              </label>

              {titSameDose ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dose</label>
                    <input
                      type="text"
                      value={titWeeks[0]?.dose || ''}
                      onChange={(e) => setTitWeeks((prev) => prev.map((w) => ({ ...w, dose: e.target.value })))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                      placeholder="e.g., 18mg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                    <input
                      type="text"
                      value={titWeeks[0]?.instructions || ''}
                      onChange={(e) => setTitWeeks((prev) => prev.map((w) => ({ ...w, instructions: e.target.value })))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                      placeholder="Instructions for the week"
                    />
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-gray-600 text-xs">
                        <th className="border-b border-gray-200 px-2 py-1 text-left w-20">Week</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Dose</th>
                        <th className="border-b border-gray-200 px-2 py-1 text-left">Instructions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {titWeeks.map((w, idx) => (
                        <tr key={idx}>
                          <td className="border-b border-gray-100 px-2 py-1 font-medium text-gray-700">Week {w.week}</td>
                          <td className="border-b border-gray-100 px-2 py-1">
                            <input
                              type="text"
                              value={w.dose}
                              onChange={(e) => setTitWeeks((prev) => prev.map((x, i) => (i === idx ? { ...x, dose: e.target.value } : x)))}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                              placeholder="e.g., 18mg"
                            />
                          </td>
                          <td className="border-b border-gray-100 px-2 py-1">
                            <input
                              type="text"
                              value={w.instructions}
                              onChange={(e) => setTitWeeks((prev) => prev.map((x, i) => (i === idx ? { ...x, instructions: e.target.value } : x)))}
                              className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                              placeholder="Instructions"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">How to take the medication (optional)</label>
                <textarea
                  value={titHowToTake}
                  onChange={(e) => setTitHowToTake(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                  placeholder="Free text…"
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Potential side effects (optional)</label>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {SIDE_EFFECT_OPTIONS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={titSideEffects.includes(opt.value)}
                        onChange={() =>
                          setTitSideEffects((prev) =>
                            prev.includes(opt.value) ? prev.filter((v) => v !== opt.value) : [...prev, opt.value]
                          )
                        }
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
                {titSideEffects.includes('other') && (
                  <input
                    type="text"
                    value={titSideEffectsOther}
                    onChange={(e) => setTitSideEffectsOther(e.target.value)}
                    className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                    placeholder="Other side effects…"
                  />
                )}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <MeetingActionBar
        meeting={{
          id: fullMeeting.id,
          team_url: fullMeeting.team_url || undefined,
          status: fullMeeting.status,
          isPSYMeeting,
          startsRecallBot: isPSYMeeting || isCMMeeting,
        }}
        onSave={requestSave}
        onFinish={requestFinish}
        onNoShow={() => onNoShow(fullMeeting)}
        onFinishTreatment={() => onFinishTreatment(fullMeeting)}
        onSendMeetingLink={() => onSendEmail(fullMeeting)}
        isSaving={isSaving}
        isFinishing={isFinishing}
        finishButtonText={isTitrationMeeting ? 'Finish' : 'Finish & Summarize'}
        finishingButtonText={isTitrationMeeting ? 'Finishing...' : 'Summarizing...'}
        additionalButtons={
          <>
            {onViewSummary && (isCMMeeting || isPSYMeeting || isTitrationMeeting) && fullMeeting.summary_ready && (
              <button
                onClick={() => onViewSummary(fullMeeting)}
                className="px-4 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
              >
                View Summary
              </button>
            )}
          </>
        }
      />


      <TreatmentDecisionConfirmDialog
        decision={pendingTreatmentAction ? treatmentDecision : null}
        busy={isSaving || isFinishing}
        onCancel={() => setPendingTreatmentAction(null)}
        onConfirm={confirmPendingTreatmentAction}
      />

      {viewedMeasurementQuestionnaire && (
        <ViewQuestionnaireModal
          isOpen
          onClose={() => setViewedMeasurementQuestionnaire(null)}
          questionnaireId={viewedMeasurementQuestionnaire.id}
          questionnaireTitle={viewedMeasurementQuestionnaire.title}
        />
      )}
    </div>
  );
};
